import React, { useRef, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import DialogBase from '../../../base/DialogBase/DialogBase';
import NewContentTypeDialog from '../NewContentTypeDialog/NewContentTypeDialog';
import DataTableNew from '../../main/DataTableNew/DataTableNew';
import { ContentRequestTypeEnum, RequestResolutionEnum, RequestStateEnum, RequestStatusEnum, useEditContentApplicationNameMutation, useEditContentCategoryMutation, useEditContentDescriptionMutation, useGetApplicationNameListQuery, useGetCategoryListQuery, useGetContentQuery, useGetContentVersionsQuery, useGetLibraryQuery, useRenameContentMutation, useRenameContentTypeMutation, useSetContentThumbnailMutation, useSetContentTypeThumbnailMutation, UserRoleEnum } from '../../../generated/graphql';
import ShowOnRole from '../../main/ShowOnRole/ShowOnRole';
import EssentialsNew from '../../main/EssentialsNew/EssentialsNew';
import DeleteContentTagDialog from '../DeleteContentTagDialog/DeleteContentTagDialog';
import EditContentSettingsDialog from '../EditContentSettingsDialog/EditContentSettingsDialog';
import { Plus, Trash2, Pencil, Box, Link2, Check, Layers, GitBranch, X, MessageCircle, Link as LinkIcon, Settings, Upload, Loader2, Info, Clock3, CheckCircle2, Eye } from 'lucide-react';
import RefreshButton from '../../main/RefreshButton/RefreshButton';
import CommentsPanel from '../../main/CommentsPanel/CommentsPanel';
import RelatedContentsPanel from '../../main/RelatedContentsPanel/RelatedContentsPanel';
import { Content } from '../../../generated/graphql';
import AddContentsClassificationsDialog from '../AddContentsClassificationsDialog/AddContentsClassificationsDialog';
import DeleteContentTypesDialog from '../DeleteContentTypesDialog/DeleteContentTypesDialog';
import JsonSettingsEditor from '../../main/JsonSettingsEditor/JsonSettingsEditor';
import AddContentsTagsDialog from '../AddContentsTagsDialog/AddContentsTagsDialog';
import MoveContentsDialog from '../MoveContentsDialog/MoveContentsDialog';
import RequestContentEditDialog from '../RequestContentEditDialog/RequestContentEditDialog';
import TagBadge from '../../main/TagBadge/TagBadge';

const GET_CONTENT_VERSION_DETAIL = gql`
    query getContentVersionDetail($id: String) {
        ContentVersion(id: $id) {
            id
            name
            description
            category
            subCategory
            applicationName
            settings
            state
            status
            resolution
            requestType
            createdTime
            updatedTime
            closedTime
            attachment { id name extension downloadUrl }
            thumbnail { id name extension downloadUrl }
            tags { id name type }
            types {
                id
                name
                description
                settings
                createdTime
                createdById
                uniclassId
                vBISId
                iFCEntityId
                thumbnail { id name extension downloadUrl }
                createdBy { id name }
            }
        }
    }
`;

const GET_CURRENT_CONTENT_VERSION_APPLICATION_NAME = gql`
    query getCurrentContentVersionApplicationName($id: String) {
        ContentVersion(id: $id) {
            id
            applicationName
        }
    }
`;

const EDIT_CONTENT_TYPE_DESCRIPTION = gql`
    mutation editEmbeddedContentTypeDescription($contentId: String, $typeId: String, $description: String) {
        Content(id: $contentId) {
            setTypeDescription(typeId: $typeId, description: $description)
        }
    }
`;

type ContentDetailDialogProps = {
    visible: boolean;
    contentId: string;
    clientId: string;
    onClose: () => void;
    defaultCommentsOpen?: boolean;
    isNested?: boolean;
};

type ClassificationChipProps = {
    id: string;
    name: string;
    description?: string | null;
    typeName?: string | null;
    isActive: boolean;
    onClick: (id: string) => void;
};

const enumLabel = (value?: string | null) => value
    ? value.toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
    : '';

const formatLifecycleDate = (value?: string | null) => {
    if (!value) return '';
    const seconds = Number(value);
    if (!Number.isFinite(seconds)) return '';
    const date = new Date(0);
    date.setUTCSeconds(seconds);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const ClassificationChip = ({ id, name, description, typeName, isActive, onClick }: ClassificationChipProps) => (
    <div className="flex flex-col">
        <button
            title={description || name}
            onClick={() => onClick(id)}
            className="badge-accent inline-flex items-center gap-1 text-left"
            style={{
                background: 'none',
                border: '1px solid var(--accent)',
                borderRadius: '6px',
                padding: '2px 8px',
                cursor: description ? 'pointer' : 'default',
                color: 'var(--accent-text)',
                fontFamily: 'inherit',
                fontSize: '0.75rem',
            }}
        >
            {typeName && <span className="font-semibold">{typeName}:</span>}
            <span>{name}</span>
        </button>
        {isActive && description && (
            <div
                className="text-xs mt-1 px-2 py-1.5 rounded-md animate-fade-in"
                style={{
                    background: 'var(--bg-subtle)',
                    border: '1px solid var(--border-default)',
                    color: 'var(--text-secondary)',
                    maxWidth: '320px',
                    lineHeight: '1.5',
                }}
            >
                {description}
            </div>
        )}
    </div>
);

const SettingCellRenderer = ({ row, contentId, isAdmin, readOnly = false, onRefetch }: { row: any; contentId: string; isAdmin: boolean; readOnly?: boolean; onRefetch: () => void }) => {
    const [editVisible, setEditVisible] = useState(false);
    return (
        <div className="flex justify-center" onClick={(event) => event.stopPropagation()}>
            <button className="btn-icon p-1 rounded" onClick={() => setEditVisible(true)} title={readOnly ? 'View settings' : 'Edit settings'}>
                {readOnly ? <Eye size={14} /> : <Pencil size={14} />}
            </button>
            {editVisible && (
                <EditContentSettingsDialog
                    isAdmin={isAdmin}
                    visible={editVisible}
                    contentId={contentId}
                    typeId={row.id}
                    settings={row.settings}
                    readOnly={readOnly}
                    onClose={() => { setEditVisible(false); onRefetch(); }}
                />
            )}
        </div>
    );
};

const ContentTypeNameCell = ({ row, contentId, isAdmin, readOnly = false, onRefetch }: { row: any; contentId: string; isAdmin: boolean; readOnly?: boolean; onRefetch: () => void }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(row.name || '');
    const [renameContentType, result] = useRenameContentTypeMutation();

    const save = async () => {
        const name = value.trim();
        if (!name) return;
        await renameContentType({ variables: { contentId, typeId: row.id, name } });
        setEditing(false);
        onRefetch();
    };

    if (editing) {
        return (
            <div className="flex items-center gap-1.5" onClick={(event) => event.stopPropagation()}>
                <input
                    className="text-sm px-2 py-1 rounded w-full"
                    style={{ border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none' }}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
                    autoFocus
                />
                <button className="btn-icon p-0.5 rounded" onClick={save} disabled={result.loading}><Check size={13} /></button>
                <button className="btn-icon p-0.5 rounded" onClick={() => { setValue(row.name || ''); setEditing(false); }}><X size={13} /></button>
            </div>
        );
    }

    return (
        <span className="flex items-center gap-1.5 group" onClick={(event) => event.stopPropagation()}>
            <span>{row.name}</span>
            {!readOnly && <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                <button className="btn-icon p-0.5 rounded opacity-0 group-hover:opacity-100" onClick={() => setEditing(true)} title="Edit type name">
                    <Pencil size={12} />
                </button>
            </ShowOnRole>}
        </span>
    );
};

const ContentTypeDescriptionCell = ({ row, contentId, isAdmin, readOnly = false, onRefetch }: { row: any; contentId: string; isAdmin: boolean; readOnly?: boolean; onRefetch: () => void }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(row.description || '');
    const [editDescription, result] = useMutation(EDIT_CONTENT_TYPE_DESCRIPTION);

    const save = async () => {
        await editDescription({ variables: { contentId, typeId: row.id, description: value.trim() } });
        setEditing(false);
        onRefetch();
    };

    if (editing) {
        return (
            <div className="flex items-center gap-1.5" onClick={(event) => event.stopPropagation()}>
                <input
                    className="text-sm px-2 py-1 rounded w-full"
                    style={{ border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none' }}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
                    autoFocus
                />
                <button className="btn-icon p-0.5 rounded" onClick={save} disabled={result.loading}><Check size={13} /></button>
                <button className="btn-icon p-0.5 rounded" onClick={() => { setValue(row.description || ''); setEditing(false); }}><X size={13} /></button>
            </div>
        );
    }

    return (
        <span className="flex items-center gap-1.5 group" onClick={(event) => event.stopPropagation()}>
            <span>{row.description || <span style={{ color: 'var(--text-tertiary)' }}>No description</span>}</span>
            {!readOnly && <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                <button className="btn-icon p-0.5 rounded opacity-0 group-hover:opacity-100" onClick={() => setEditing(true)} title="Edit type description">
                    <Pencil size={12} />
                </button>
            </ShowOnRole>}
        </span>
    );
};

const ContentTypeThumbnailCell = ({ row, contentId, isAdmin, readOnly = false, onRefetch }: { row: any; contentId: string; isAdmin: boolean; readOnly?: boolean; onRefetch: () => void }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [setContentTypeThumbnail] = useSetContentTypeThumbnailMutation();
    const thumbnailUrl = row?.thumbnail?.downloadUrl;

    const handleSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        setError('');
        setUploading(true);
        try {
            const result = await setContentTypeThumbnail({ variables: { contentId, typeId: row.id, name: file.name } });
            const uploadUrl = result.data?.Content?.setTypeThumbnail;
            if (!uploadUrl) throw new Error('The server did not return a thumbnail upload URL.');

            const response = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'x-ms-blob-type': 'BlockBlob' },
                body: file,
            });
            if (!response.ok) throw new Error(`Thumbnail upload failed (${response.status}).`);
            onRefetch();
        } catch (uploadError) {
            setError(uploadError instanceof Error ? uploadError.message : 'Unable to upload the thumbnail.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="relative group inline-flex" title={error || (thumbnailUrl ? 'Content Type thumbnail' : 'No thumbnail')}>
            {thumbnailUrl ? (
                <img height="40" width="40" className="rounded object-cover border" style={{ borderColor: error ? 'var(--danger)' : 'var(--border-default)' }} src={thumbnailUrl} alt={`${row.name || 'Content Type'} thumbnail`} />
            ) : (
                <div className="w-10 h-10 rounded border flex items-center justify-center" style={{ borderColor: error ? 'var(--danger)' : 'var(--border-default)', borderStyle: 'dashed', color: 'var(--text-tertiary)' }}>
                    <Upload size={14} />
                </div>
            )}
            {!readOnly && <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                <button
                    type="button"
                    className={`absolute inset-0 rounded flex items-center justify-center transition-opacity ${thumbnailUrl && !uploading ? 'opacity-0 group-hover:opacity-100' : ''}`}
                    style={{ backgroundColor: thumbnailUrl ? 'rgba(0,0,0,0.55)' : 'transparent', color: thumbnailUrl ? 'white' : 'var(--accent-text)', border: 'none', cursor: uploading ? 'wait' : 'pointer' }}
                    disabled={uploading}
                    onClick={(event) => { event.stopPropagation(); inputRef.current?.click(); }}
                    title={thumbnailUrl ? 'Replace Content Type thumbnail' : 'Add Content Type thumbnail'}
                    aria-label={thumbnailUrl ? 'Replace Content Type thumbnail' : 'Add Content Type thumbnail'}
                >
                    {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                </button>
            </ShowOnRole>}
            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleSelected} />
        </div>
    );
};

const ContentDetailDialog = (props: ContentDetailDialogProps) => {
    const { visible, contentId, clientId, onClose, isNested } = props;
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState(0);
    const [, setSearchText] = useState("");
    const [searchVersionText, setSearchVersionText] = useState("");
    const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [newTypeVisible, setNewTypeVisible] = useState(false);
    const [deleteTypesVisible, setDeleteTypesVisible] = useState(false);
    const [typeIdsToDelete, setTypeIdsToDelete] = useState<string[]>([]);
    const [deleteContentTagVisible, setDeleteContentTagVisible] = useState(false);
    const [selectedTagId, setSelectedTagId] = useState("");
    const [linkCopied, setLinkCopied] = useState(false);
    const [manageClassificationsVisible, setManageClassificationsVisible] = useState(false);
    const [activeClassificationId, setActiveClassificationId] = useState<string | null>(null);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editNameValue, setEditNameValue] = useState('');
    const [commentsOpen, setCommentsOpen] = useState(props.defaultCommentsOpen ?? false);
    const [contentSettingsVisible, setContentSettingsVisible] = useState(false);
    const [assignTagsVisible, setAssignTagsVisible] = useState(false);
    const [moveContentVisible, setMoveContentVisible] = useState(false);
    const [requestEditVisible, setRequestEditVisible] = useState(false);
    const [isEditingCategory, setIsEditingCategory] = useState(false);
    const [editCategoryValue, setEditCategoryValue] = useState('');
    const [isEditingApplicationName, setIsEditingApplicationName] = useState(false);
    const [editApplicationNameValue, setEditApplicationNameValue] = useState('');
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editDescriptionValue, setEditDescriptionValue] = useState('');
    const [thumbnailError, setThumbnailError] = useState('');
    const [thumbnailUploading, setThumbnailUploading] = useState(false);
    const thumbnailInputRef = useRef<HTMLInputElement>(null);
    const [renameContent, renameContentResult] = useRenameContentMutation();
    const [editContentCategory, editContentCategoryResult] = useEditContentCategoryMutation();
    const [editContentApplicationName, editContentApplicationNameResult] = useEditContentApplicationNameMutation();
    const [editContentDescription, editContentDescriptionResult] = useEditContentDescriptionMutation();
    const [setContentThumbnail] = useSetContentThumbnailMutation();
    const categoryList = useGetCategoryListQuery({ skip: !isEditingCategory });
    const categoryOptions = (categoryList.data?.CategoryList || [])
        .filter((category): category is string => !!category)
        .sort((a, b) => a.localeCompare(b));
    const applicationNameList = useGetApplicationNameListQuery({ skip: !isEditingApplicationName });
    const applicationNameOptions = (applicationNameList.data?.ApplicationNameList || [])
        .filter((applicationName): applicationName is string => !!applicationName)
        .sort((a, b) => a.localeCompare(b));

    const handleClassificationClick = (id: string) => {
        setActiveClassificationId(prev => prev === id ? null : id);
    };

    const content = useGetContentQuery({
        variables: { id: contentId }
    });

    const currentVersionResult = useQuery(GET_CURRENT_CONTENT_VERSION_APPLICATION_NAME, {
        variables: { id: content.data?.Content?.currentVersionId },
        skip: !content.data?.Content?.currentVersionId,
        fetchPolicy: 'network-only',
    });

    const library = useGetLibraryQuery({
        variables: { id: content.data?.Content?.libraryId }
    });

    const contentVersion = useGetContentVersionsQuery({
        variables: {
            contentId: contentId,
            contains: searchVersionText
        }
    });

    const selectedVersionResult = useQuery(GET_CONTENT_VERSION_DETAIL, {
        variables: { id: selectedVersionId },
        skip: !selectedVersionId,
        fetchPolicy: 'network-only',
    });

    const handleSaveName = async () => {
        if (!editNameValue.trim()) return;
        try {
            await renameContent({ variables: { id: contentId, name: editNameValue.trim() } });
            setIsEditingName(false);
            content.refetch();
        } catch {
            // handled by mutation result
        }
    };

    const handleOnClose = () => {
        setNewTypeVisible(false);
        setDeleteContentTagVisible(false);
        setDeleteTypesVisible(false);
        setTypeIdsToDelete([]);
        setSelectedIds([]);
        content.refetch();
    };

    const handleSaveCategory = async () => {
        const versionId = content.data?.Content?.currentVersionId;
        if (!versionId || !editCategoryValue.trim()) return;
        try {
            await editContentCategory({ variables: { versionId, category: editCategoryValue.trim() } });
            setIsEditingCategory(false);
            content.refetch();
        } catch {
            // handled by mutation result
        }
    };

    const handleSaveApplicationName = async () => {
        if (!contentId || !editApplicationNameValue.trim()) return;
        try {
            await editContentApplicationName({ variables: { id: contentId, applicationName: editApplicationNameValue.trim() } });
            setIsEditingApplicationName(false);
            await Promise.all([content.refetch(), currentVersionResult.refetch()]);
        } catch {
            // handled by mutation result
        }
    };

    const handleSaveDescription = async () => {
        try {
            await editContentDescription({ variables: { id: contentId, description: editDescriptionValue.trim() } });
            setIsEditingDescription(false);
            content.refetch();
        } catch {
            // handled by mutation result
        }
    };

    const handleThumbnailSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        setThumbnailError('');
        setThumbnailUploading(true);
        try {
            const result = await setContentThumbnail({ variables: { contentId, name: file.name } });
            const uploadUrl = result.data?.Content?.setThumbnail;
            if (!uploadUrl) throw new Error('The server did not return a thumbnail upload URL.');

            const response = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'x-ms-blob-type': 'BlockBlob' },
                body: file,
            });
            if (!response.ok) throw new Error(`Thumbnail upload failed (${response.status}).`);

            await content.refetch();
        } catch (error) {
            setThumbnailError(error instanceof Error ? error.message : 'Unable to upload the thumbnail.');
        } finally {
            setThumbnailUploading(false);
        }
    };

    const handleDeleteTypes = (ids: string[]) => {
        setTypeIdsToDelete(ids);
        setDeleteTypesVisible(true);
    };

    const handleOnRemoveTag = (tagId: string) => {
        setSelectedTagId(tagId);
        setDeleteContentTagVisible(true);
    };

    const handleCopyLink = () => {
        const libraryId = content.data?.Content?.libraryId;
        const url = new URL(window.location.origin + `/clients/${clientId}/library/${libraryId}`);
        url.searchParams.set('contentId', contentId);
        navigator.clipboard.writeText(url.toString());
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
    };

    const tabs = [
        { label: 'Essentials', icon: Info },
        { label: 'Settings', icon: Settings },
        { label: 'Types', icon: Layers },
        { label: 'Versions', icon: GitBranch },
        { label: 'Related Content', icon: LinkIcon },
    ];

    const versions = (contentVersion.data?.ContentVersionList?.nodes || [])
        .filter((version): version is NonNullable<typeof version> => !!version)
        .sort((left, right) => {
            const leftOpen = left.state === RequestStateEnum.Open || left.id === content.data?.Content?.currentRequestId;
            const rightOpen = right.state === RequestStateEnum.Open || right.id === content.data?.Content?.currentRequestId;
            if (leftOpen !== rightOpen) return leftOpen ? -1 : 1;
            return Number(right.closedTime || right.updatedTime || right.createdTime || 0)
                - Number(left.closedTime || left.updatedTime || left.createdTime || 0);
        });

    const isViewingHistoricalVersion = !!selectedVersionId;
    const selectedVersion = selectedVersionResult.data?.ContentVersion;
    const currentVersionApplicationName = currentVersionResult.data?.ContentVersion?.applicationName;
    const displayedName = isViewingHistoricalVersion ? selectedVersion?.name : content.data?.Content?.name;
    const displayedCategory = isViewingHistoricalVersion ? selectedVersion?.category : content.data?.Content?.category;
    const displayedApplicationName = isViewingHistoricalVersion ? selectedVersion?.applicationName : currentVersionApplicationName;
    const displayedDescription = isViewingHistoricalVersion ? selectedVersion?.description : content.data?.Content?.description;
    const displayedSettings = isViewingHistoricalVersion ? selectedVersion?.settings : content.data?.Content?.settings;
    const displayedTypes = isViewingHistoricalVersion ? selectedVersion?.types : content.data?.Content?.types;
    const displayedThumbnail = isViewingHistoricalVersion ? selectedVersion?.thumbnail?.downloadUrl : content.data?.Content?.thumbnail?.downloadUrl;
    const displayedTags = isViewingHistoricalVersion ? selectedVersion?.tags : content.data?.Content?.tags;
    const hasFile = isViewingHistoricalVersion ? !!selectedVersion?.attachment : !!content.data?.Content?.file;
    const showComments = hasFile && commentsOpen;
    const canEditCurrentVersion = !isViewingHistoricalVersion;

    const selectVersion = (versionId?: string | null) => {
        if (!versionId || versionId === content.data?.Content?.currentVersionId) {
            setSelectedVersionId(null);
        } else {
            setSelectedVersionId(versionId);
        }
        setActiveTab(0);
        setCommentsOpen(false);
        setSelectedIds([]);
        setIsEditingName(false);
        setIsEditingCategory(false);
        setIsEditingApplicationName(false);
        setIsEditingDescription(false);
    };

    return (
        <DialogBase
            title={
                <span className="flex items-center gap-2.5">
                    <span
                        className="flex items-center justify-center rounded-lg"
                        style={{ width: 30, height: 30, backgroundColor: 'var(--accent-soft)', color: 'var(--accent)' }}
                    >
                        <Box size={16} />
                    </span>
                    <span style={{ color: 'var(--text-primary)' }}>{content.data?.Content?.name || "Content"}</span>
                </span>
            }
            visible={visible}
            type="Custom"
            onClose={onClose}
            width={showComments ? '1280px' : '900px'}
            style={{ transition: 'width 0.3s ease' }}
            isLoading={content.loading && !content.data}
            headerActions={
                <>
                    {canEditCurrentVersion && hasFile && (
                        <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={false}>
                            <button
                                onClick={() => setRequestEditVisible(true)}
                                disabled={!!content.data?.Content?.currentRequestId}
                                className="btn-secondary text-xs"
                                title={content.data?.Content?.currentRequestId ? 'This content already has an open request' : 'Request an edit'}
                            >
                                <Upload size={14} />
                                {content.data?.Content?.currentRequestId ? 'Request Open' : 'Request Edit'}
                                <span
                                    className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                                    style={{ background: 'var(--accent-soft)', color: 'var(--accent-text)', letterSpacing: '0.04em', lineHeight: 1 }}
                                >
                                    Preview
                                </span>
                            </button>
                        </ShowOnRole>
                    )}
                    {hasFile && (
                        <button
                            onClick={() => setCommentsOpen(prev => !prev)}
                            className="p-1.5 rounded-lg transition-colors duration-150"
                            style={{
                                background: commentsOpen ? 'var(--accent-soft)' : 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: commentsOpen ? 'var(--accent)' : 'var(--text-tertiary)',
                            }}
                            onMouseOver={(e) => { if (!commentsOpen) e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                            onMouseOut={(e) => { if (!commentsOpen) e.currentTarget.style.backgroundColor = 'transparent'; }}
                            aria-label="Toggle comments"
                            title="Toggle comments"
                        >
                            <MessageCircle size={16} />
                        </button>
                    )}
                    <button
                        onClick={handleCopyLink}
                        className="p-1.5 rounded-lg transition-colors duration-150"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: linkCopied ? 'var(--accent)' : 'var(--text-tertiary)' }}
                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        aria-label="Copy link"
                        title="Copy link to clipboard"
                    >
                        {linkCopied ? <Check size={16} /> : <Link2 size={16} />}
                    </button>
                </>
            }
        >
            <div className="flex" style={{ height: 'min(620px, calc(85vh - 140px))', margin: '-20px -24px', overflow: 'hidden' }}>
                {/* Main content */}
                <div className={`flex flex-col ${showComments ? 'flex-1 min-w-0' : 'w-full'}`} style={{ transition: 'all 0.3s ease', overflow: 'hidden' }}>
                <div className="px-4 pt-3 pb-2 border-b" style={{ borderColor: 'var(--border-default)', flexShrink: 0 }}>
                    <div className="tab-bar" style={{ alignSelf: 'flex-start', display: 'inline-flex' }}>
                        {tabs.map((tab, index) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.label}
                                    onClick={() => {
                                        setActiveTab(index);
                                        if (index === 4) content.refetch();
                                        if (index === 3) contentVersion.refetch();
                                    }}
                                    className={activeTab === index ? 'tab-item-active' : 'tab-item'}
                                >
                                    <Icon size={14} strokeWidth={1.5} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {isViewingHistoricalVersion && (
                    <div
                        className="flex items-center justify-between gap-3 px-4 py-2 border-b text-sm"
                        style={{ borderColor: 'var(--accent)', backgroundColor: 'var(--accent-soft)', color: 'var(--accent-text)', flexShrink: 0 }}
                    >
                        <span>
                            {selectedVersionResult.error
                                ? `Unable to load the selected version: ${selectedVersionResult.error.message}`
                                : selectedVersionResult.loading
                                ? 'Loading selected version…'
                                : <>Viewing historical version <strong>{selectedVersion?.name || selectedVersionId}</strong>. Editing is disabled.</>}
                        </span>
                        <button className="btn btn-ghost text-xs" onClick={() => selectVersion(null)}>
                            Return to current version
                        </button>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto min-h-0">
                {/* Essentials */}
                {activeTab === 0 && (
                <EssentialsNew
                    persistenceKey="content-detail-dialog"
                    className="mx-4 mt-3"
                    isAdmin={canEditCurrentVersion && (content.data?.Content?.library?.isAdmin || false)}
                    onRemoveTag={canEditCurrentVersion ? handleOnRemoveTag : undefined}
                    preview={displayedThumbnail || ""}
                    onEditPreview={canEditCurrentVersion ? () => thumbnailInputRef.current?.click() : undefined}
                    previewUploading={thumbnailUploading}
                    rows={[
                        { title: "Name", value: (
                            isEditingName ? (
                                <div className="flex items-center gap-1.5">
                                    <input
                                        className="text-sm px-2 py-0.5 rounded"
                                        style={{ border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none', minWidth: '180px' }}
                                        value={editNameValue}
                                        onChange={(e) => setEditNameValue(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setIsEditingName(false); }}
                                        autoFocus
                                    />
                                    <button className="btn-icon p-0.5 rounded" onClick={handleSaveName} disabled={renameContentResult.loading}>
                                        <Check size={13} />
                                    </button>
                                    <button className="btn-icon p-0.5 rounded" onClick={() => setIsEditingName(false)}>
                                        <X size={13} />
                                    </button>
                                </div>
                            ) : (
                                <span className="flex items-center gap-1.5 group">
                                    <span>{displayedName || ""}</span>
                                    {canEditCurrentVersion && <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={content.data?.Content?.library?.isAdmin || false}>
                                        <button
                                            className="btn-icon p-0.5 rounded opacity-0 group-hover:opacity-100"
                                            style={{ transition: 'opacity 0.15s' }}
                                            onClick={() => { setEditNameValue(content?.data?.Content?.name || ''); setIsEditingName(true); }}
                                        >
                                            <Pencil size={12} />
                                        </button>
                                    </ShowOnRole>}
                                </span>
                            )
                        ) },
                        { title: "Category", value: (
                            isEditingCategory ? (
                                <div className="flex items-center gap-1.5">
                                    <input
                                        className="text-sm px-2 py-0.5 rounded"
                                        style={{ border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none', minWidth: '180px' }}
                                        value={editCategoryValue}
                                        onChange={(e) => setEditCategoryValue(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCategory(); if (e.key === 'Escape') setIsEditingCategory(false); }}
                                        list={`content-category-options-${contentId}`}
                                        placeholder={categoryList.loading ? 'Loading categories…' : 'Select or enter a category'}
                                        autoFocus
                                    />
                                    <datalist id={`content-category-options-${contentId}`}>
                                        {categoryOptions.map(category => <option key={category} value={category} />)}
                                    </datalist>
                                    <button className="btn-icon p-0.5 rounded" onClick={handleSaveCategory} disabled={editContentCategoryResult.loading}><Check size={13} /></button>
                                    <button className="btn-icon p-0.5 rounded" onClick={() => setIsEditingCategory(false)}><X size={13} /></button>
                                </div>
                            ) : (
                                <span className="flex items-center gap-1.5 group">
                                    <span>{displayedCategory || ""}</span>
                                    {canEditCurrentVersion && content.data?.Content?.currentVersionId && (
                                        <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={content.data?.Content?.library?.isAdmin || false}>
                                            <button className="btn-icon p-0.5 rounded opacity-0 group-hover:opacity-100" onClick={() => { setEditCategoryValue(content.data?.Content?.category || ''); setIsEditingCategory(true); }} title="Edit category">
                                                <Pencil size={12} />
                                            </button>
                                        </ShowOnRole>
                                    )}
                                </span>
                            )
                        ) },
                        { title: "Application Name", value: (
                            isEditingApplicationName ? (
                                <div className="flex items-center gap-1.5">
                                    <input
                                        className="text-sm px-2 py-0.5 rounded"
                                        style={{ border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none', minWidth: '180px' }}
                                        value={editApplicationNameValue}
                                        onChange={(e) => setEditApplicationNameValue(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveApplicationName(); if (e.key === 'Escape') setIsEditingApplicationName(false); }}
                                        list={`content-application-name-options-${contentId}`}
                                        placeholder={applicationNameList.loading ? 'Loading applications...' : 'Select or enter an application'}
                                        autoFocus
                                    />
                                    <datalist id={`content-application-name-options-${contentId}`}>
                                        {applicationNameOptions.map(applicationName => <option key={applicationName} value={applicationName} />)}
                                    </datalist>
                                    <button className="btn-icon p-0.5 rounded" onClick={handleSaveApplicationName} disabled={editContentApplicationNameResult.loading}><Check size={13} /></button>
                                    <button className="btn-icon p-0.5 rounded" onClick={() => setIsEditingApplicationName(false)}><X size={13} /></button>
                                </div>
                            ) : (
                                <span className="flex items-center gap-1.5 group">
                                    <span>{displayedApplicationName || <span style={{ color: 'var(--text-tertiary)' }}>No application</span>}</span>
                                    {canEditCurrentVersion && content.data?.Content?.currentVersionId && (
                                        <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={content.data?.Content?.library?.isAdmin || false}>
                                            <button className="btn-icon p-0.5 rounded opacity-0 group-hover:opacity-100" onClick={() => { setEditApplicationNameValue(currentVersionApplicationName || ''); setIsEditingApplicationName(true); }} title="Edit application name">
                                                <Pencil size={12} />
                                            </button>
                                        </ShowOnRole>
                                    )}
                                </span>
                            )
                        ) },
                        { title: "Library", value: (
                            <span className="flex items-center gap-1.5 group">
                                <button
                                    className="hover:underline text-left"
                                    style={{ color: 'var(--accent-text)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
                                    onClick={() => { onClose(); navigate(`/clients/${clientId}/library/${content?.data?.Content?.libraryId}`, { state: { tab: 'contents' } }); }}
                                >
                                    {content?.data?.Content?.library?.name || ""}
                                </button>
                                {canEditCurrentVersion && !isNested && (
                                    <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={content.data?.Content?.library?.isAdmin || false}>
                                        <button className="btn-icon p-0.5 rounded opacity-0 group-hover:opacity-100" onClick={() => setMoveContentVisible(true)} title="Move content">
                                            <Pencil size={12} />
                                        </button>
                                    </ShowOnRole>
                                )}
                            </span>
                        ) },
                        { title: "Description", className: "md:col-span-2", value: (
                            isEditingDescription ? (
                                <div className="flex items-start gap-1.5 min-w-0 w-full">
                                    <textarea
                                        className="text-sm px-2 py-1 rounded flex-1 min-w-0"
                                        style={{ border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none', minHeight: '64px', resize: 'vertical' }}
                                        value={editDescriptionValue}
                                        onChange={(e) => setEditDescriptionValue(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Escape') setIsEditingDescription(false); if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSaveDescription(); }}
                                        autoFocus
                                    />
                                    <button className="btn-icon p-0.5 rounded flex-shrink-0" onClick={handleSaveDescription} disabled={editContentDescriptionResult.loading} title="Save description" aria-label="Save description"><Check size={13} /></button>
                                    <button className="btn-icon p-0.5 rounded flex-shrink-0" onClick={() => setIsEditingDescription(false)} title="Cancel editing description" aria-label="Cancel editing description"><X size={13} /></button>
                                </div>
                            ) : (
                                <span className="flex items-center gap-1.5 group">
                                    <span>{displayedDescription || <span style={{ color: 'var(--text-tertiary)' }}>No description</span>}</span>
                                    {canEditCurrentVersion && <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={content.data?.Content?.library?.isAdmin || false}>
                                        <button className="btn-icon p-0.5 rounded opacity-0 group-hover:opacity-100" onClick={() => { setEditDescriptionValue(content.data?.Content?.description || ''); setIsEditingDescription(true); }} title="Edit description">
                                            <Pencil size={12} />
                                        </button>
                                    </ShowOnRole>}
                                </span>
                            )
                        ) },
                        { title: "Tags", value: (
                            <div className="flex flex-wrap items-center gap-1.5">
                                {(displayedTags || []).filter(Boolean).map((t: any) => (
                                    <TagBadge key={t!.id} tag={t} className="inline-flex items-center gap-1">
                                        <span className="font-semibold">{t!.type}:</span><span>{t!.name}</span>
                                        {canEditCurrentVersion && <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={content.data?.Content?.library?.isAdmin || false}>
                                            <button onClick={() => handleOnRemoveTag(t!.id || '')} className="ml-0.5 rounded-full hover:opacity-70" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }} title="Remove tag"><X size={10} /></button>
                                        </ShowOnRole>}
                                    </TagBadge>
                                ))}
                                {canEditCurrentVersion && !isNested && (
                                    <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={content.data?.Content?.library?.isAdmin || false}>
                                        <button className="btn-icon p-0.5 rounded" onClick={() => setAssignTagsVisible(true)} title="Manage tags"><Plus size={13} /></button>
                                    </ShowOnRole>
                                )}
                            </div>
                        ) },
                        { title: "Classifications", value: (
                            <div className="flex flex-wrap items-center gap-1.5">
                                {content?.data?.Content?.classifications?.length
                                    ? content.data.Content.classifications.filter(Boolean).map(c => (
                                        <ClassificationChip
                                            key={c!.id!}
                                            id={c!.id!}
                                            name={c!.name || ""}
                                            description={c!.description}
                                            typeName={c!.type?.name}
                                            isActive={activeClassificationId === c!.id}
                                            onClick={handleClassificationClick}
                                        />
                                    ))
                                    : <span style={{ color: 'var(--text-tertiary)' }}>—</span>
                                }
                                {canEditCurrentVersion && !isNested && (
                                    <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={content.data?.Content?.library?.isAdmin || false}>
                                        <button className="btn-icon p-0.5 rounded" style={{ flexShrink: 0 }} onClick={() => setManageClassificationsVisible(true)} title="Manage classifications"><Plus size={13} /></button>
                                    </ShowOnRole>
                                )}
                            </div>
                        ) },
                    ]}
                >
                    {thumbnailError && <p className="mt-3 text-xs" style={{ color: 'var(--danger)' }}>{thumbnailError}</p>}
                </EssentialsNew>
                )}

                {activeTab === 1 && !content.loading && (
                    <section className="mx-4 mt-4 mb-3 rounded-lg border" style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)' }}>
                        <div className="flex items-center justify-between gap-4 px-4 py-3 border-b" style={{ borderColor: 'var(--border-default)' }}>
                            <div>
                                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Content settings</h3>
                                <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                                    {isViewingHistoricalVersion
                                        ? 'Read-only configuration stored with the selected historical version.'
                                        : hasFile ? 'Configuration stored with the current content version.' : 'This content has no attached file. Its configuration is shown below.'}
                                </p>
                            </div>
                            {canEditCurrentVersion && <button className="btn btn-ghost text-sm" onClick={() => setContentSettingsVisible(true)}>
                                <Pencil size={14} /> Edit settings
                            </button>}
                        </div>
                        <JsonSettingsEditor
                            visualizeOnly
                            editable={false}
                            style={{ minHeight: '220px', maxHeight: '420px' }}
                            settings={displayedSettings || ''}
                            onDataChange={() => undefined}
                        />
                    </section>
                )}

                {/* Tab content */}
                <div className="flex-1 flex flex-col" style={{ minHeight: '250px' }}>
                    {activeTab === 2 && (
                        <>
                            <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                                <RefreshButton onRefresh={() => isViewingHistoricalVersion ? selectedVersionResult.refetch() : content.refetch()} />
                                {canEditCurrentVersion && <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={content.data?.Content?.library?.isAdmin || false}>
                                    <button className="btn btn-ghost text-sm" onClick={() => setNewTypeVisible(true)}>
                                        <Plus size={15} /> Add Type
                                    </button>
                                    {selectedIds.length > 0 && (
                                        <>
                                            <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />
                                            <button className="btn btn-ghost text-sm" style={{ color: 'var(--danger)' }} onClick={() => handleDeleteTypes(selectedIds)}>
                                                <Trash2 size={15} /> Trash ({selectedIds.length})
                                            </button>
                                        </>
                                    )}
                                </ShowOnRole>}
                            </div>
                            <div className="flex-1" style={{ minHeight: '200px' }}>
                                <DataTableNew
                                    sortingMode="client"
                                    showName={false}
                                    isLoading={content.loading || selectedVersionResult.loading}
                                    data={displayedTypes}
                                    onSearchChanged={(value) => setSearchText(value)}
                                    onSelected={(ids) => setSelectedIds(ids)}
                                    columns={[
                                        { id: 'image', name: 'Image', width: '70px', render: (row) => <ContentTypeThumbnailCell row={row} contentId={contentId} isAdmin={library.data?.Library?.isAdmin || false} readOnly={isViewingHistoricalVersion} onRefetch={() => content.refetch()} /> },
                                        { id: 'name', name: 'Name', sortable: true, render: (row) => <ContentTypeNameCell row={row} contentId={contentId} isAdmin={library.data?.Library?.isAdmin || false} readOnly={isViewingHistoricalVersion} onRefetch={() => content.refetch()} /> },
                                        { id: 'description', name: 'Description', sortable: true, render: (row) => <ContentTypeDescriptionCell row={row} contentId={contentId} isAdmin={library.data?.Library?.isAdmin || false} readOnly={isViewingHistoricalVersion} onRefetch={() => content.refetch()} /> },
                                        { id: 'settings', name: 'Settings', width: '78px', render: (row) => <SettingCellRenderer row={row} contentId={contentId} isAdmin={library.data?.Library?.isAdmin || false} readOnly={isViewingHistoricalVersion} onRefetch={() => content.refetch()} /> },
                                        {
                                            id: 'trash',
                                            name: '',
                                            width: '56px',
                                            render: (row) => (
                                                canEditCurrentVersion ? <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={content.data?.Content?.library?.isAdmin || false}>
                                                    <button
                                                        className="btn-icon p-1 rounded"
                                                        style={{ color: 'var(--danger)' }}
                                                        title="Trash Type"
                                                        aria-label={`Trash ${row.name || 'content type'}`}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            handleDeleteTypes([row.id]);
                                                        }}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </ShowOnRole> : null
                                            ),
                                        },
                                    ]}
                                />
                            </div>
                        </>
                    )}
                    {activeTab === 3 && (
                        <>
                            <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: 'var(--border-default)' }}>
                                <RefreshButton onRefresh={() => contentVersion.refetch()} />
                                <div className="relative flex-1 max-w-sm">
                                    <input
                                        className="w-full text-sm px-3 py-1.5 rounded-md"
                                        style={{ border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none' }}
                                        value={searchVersionText}
                                        onChange={(event) => setSearchVersionText(event.target.value)}
                                        placeholder="Search versions and requests"
                                    />
                                </div>
                            </div>
                            <div className="flex-1 px-4 py-4" style={{ minHeight: '200px' }}>
                                {contentVersion.loading ? (
                                    <div className="flex justify-center py-10" style={{ color: 'var(--text-tertiary)' }}><Loader2 className="animate-spin" size={20} /></div>
                                ) : versions.length === 0 ? (
                                    <div className="text-center py-10 text-sm" style={{ color: 'var(--text-tertiary)' }}>No versions or requests found.</div>
                                ) : (
                                    <div className="relative">
                                        <div className="absolute" style={{ left: '15px', top: '18px', bottom: '18px', width: '2px', background: 'var(--border-default)' }} />
                                        <div className="flex flex-col gap-3">
                                            {versions.map((version) => {
                                                const isOpen = version.state === RequestStateEnum.Open || version.id === content.data?.Content?.currentRequestId;
                                                const isCurrent = version.id === content.data?.Content?.currentVersionId;
                                                const isSelected = version.id === selectedVersionId;
                                                const isApproved = version.status === RequestStatusEnum.Approved
                                                    || version.resolution === RequestResolutionEnum.Published
                                                    || isCurrent;
                                                const state = version.state || (isApproved ? RequestStateEnum.Closed : undefined);
                                                const approvedDate = isApproved ? (version.closedTime || version.updatedTime) : undefined;
                                                const lifecycleDate = formatLifecycleDate(approvedDate || version.updatedTime || version.createdTime);
                                                const dateLabel = approvedDate
                                                    ? 'Approved'
                                                    : isOpen
                                                        ? 'Updated'
                                                        : approvedDate
                                                            ? 'Closed'
                                                            : 'Created';
                                                const requestType = version.requestType === ContentRequestTypeEnum.EditContent
                                                    ? 'Edit request'
                                                    : version.requestType === ContentRequestTypeEnum.NewContent
                                                        ? 'New content request'
                                                        : version.requestType === ContentRequestTypeEnum.General
                                                            ? 'General request'
                                                            : null;

                                                return (
                                                    <div key={version.id || `${version.name}-${version.createdTime}`} className="relative flex gap-3">
                                                        <div
                                                            className="relative z-10 flex items-center justify-center rounded-full mt-3"
                                                            style={{
                                                                width: 32,
                                                                height: 32,
                                                                flexShrink: 0,
                                                                background: isOpen ? 'var(--accent-soft)' : 'var(--bg-surface)',
                                                                border: `2px solid ${isOpen || isCurrent ? 'var(--accent)' : 'var(--border-default)'}`,
                                                                color: isOpen || isCurrent ? 'var(--accent)' : 'var(--text-tertiary)',
                                                            }}
                                                        >
                                                            {isOpen ? <Clock3 size={15} /> : <CheckCircle2 size={15} />}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="flex-1 rounded-lg border px-4 py-3 text-left transition-colors"
                                                            onClick={() => selectVersion(version.id)}
                                                            title={isCurrent ? 'Return to the current version' : 'View this version'}
                                                            style={{
                                                                borderColor: isOpen || isCurrent || isSelected ? 'var(--accent)' : 'var(--border-default)',
                                                                background: isOpen || isSelected ? 'var(--accent-soft)' : 'var(--bg-surface)',
                                                                color: 'inherit',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            <div className="flex items-start justify-between gap-4">
                                                                <div className="min-w-0">
                                                                    <div className="flex flex-wrap items-center gap-2">
                                                                        <h3 className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{version.name || 'Unnamed version'}</h3>
                                                                        {isOpen && <span className="badge-accent">Ahead of current version</span>}
                                                                        {isCurrent && <span className="badge-accent">Current version</span>}
                                                                        {isSelected && <span className="badge-accent">Viewing</span>}
                                                                    </div>
                                                                    {version.description && <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{version.description}</p>}
                                                                </div>
                                                                <div className="flex flex-wrap justify-end gap-1.5" style={{ flexShrink: 0 }}>
                                                                    {state && <span className="badge-accent">{enumLabel(state)}</span>}
                                                                    {version.status && <span className="badge-accent">{enumLabel(version.status)}</span>}
                                                                    {version.resolution && <span className="badge-accent">{enumLabel(version.resolution)}</span>}
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                                                {requestType && <span>{requestType}</span>}
                                                                {version.createdBy?.name && <span>Created by {version.createdBy.name}</span>}
                                                                {lifecycleDate && (
                                                                    <span>{dateLabel} {lifecycleDate}</span>
                                                                )}
                                                            </div>
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    {activeTab === 4 && (
                        <RelatedContentsPanel
                            contentId={contentId}
                            relatedContents={(content.data?.Content?.relatedContents || []).filter(Boolean) as Partial<Content>[]}
                            isAdmin={content.data?.Content?.library?.isAdmin || false}
                            onRefetch={() => content.refetch()}
                        />
                    )}
                </div>
                </div>
            </div>

                {/* Comments side panel */}
                {showComments && (
                    <div style={{ width: '340px', flexShrink: 0, transition: 'width 0.3s ease' }}>
                        <CommentsPanel
                            contentId={contentId}
                            currentVersionId={selectedVersionId || content.data?.Content?.currentRequestId || content.data?.Content?.currentVersionId}
                        />
                    </div>
                )}
            </div>

            {canEditCurrentVersion && newTypeVisible && <NewContentTypeDialog contentId={contentId} visible={newTypeVisible} onClose={handleOnClose} />}
            <input ref={thumbnailInputRef} type="file" accept="image/*" className="hidden" onChange={handleThumbnailSelected} />
            {deleteTypesVisible && <DeleteContentTypesDialog contentId={contentId} ids={typeIdsToDelete} visible={deleteTypesVisible} onClose={handleOnClose} />}
            {deleteContentTagVisible && <DeleteContentTagDialog id={contentId} tagId={selectedTagId} visible={deleteContentTagVisible} onClose={handleOnClose} />}
            {assignTagsVisible && <AddContentsTagsDialog libraryId={content.data?.Content?.libraryId || ''} ids={[contentId]} visible={assignTagsVisible} onClose={() => { setAssignTagsVisible(false); content.refetch(); }} />}
            {moveContentVisible && <MoveContentsDialog libraryId={content.data?.Content?.libraryId || ''} ids={[contentId]} visible={moveContentVisible} onClose={() => { setMoveContentVisible(false); content.refetch(); }} />}
            {canEditCurrentVersion && contentSettingsVisible && (
                <EditContentSettingsDialog
                    isAdmin={content.data?.Content?.library?.isAdmin || false}
                    visible={contentSettingsVisible}
                    contentId={contentId}
                    currentVersionId={content.data?.Content?.currentVersionId || undefined}
                    settings={content.data?.Content?.settings || ''}
                    onClose={() => { setContentSettingsVisible(false); content.refetch(); }}
                />
            )}
            {canEditCurrentVersion && requestEditVisible && content.data?.Content?.id && hasFile && (
                <RequestContentEditDialog
                    visible={requestEditVisible}
                    contentId={content.data.Content.id}
                    contentName={content.data.Content.name}
                    onClose={() => { setRequestEditVisible(false); content.refetch(); }}
                />
            )}
            {!isNested && manageClassificationsVisible && (
                <AddContentsClassificationsDialog
                    libraryId={content.data?.Content?.libraryId || ''}
                    ids={[contentId]}
                    visible={manageClassificationsVisible}
                    onClose={() => { setManageClassificationsVisible(false); content.refetch(); }}
                />
            )}
        </DialogBase>
    );
};

export default ContentDetailDialog;
